import { defineStore } from "pinia"
import { ref, computed } from "vue"

import axios from "@axios"
import { reportError } from "@/plugins/misc"
import { cmsMessagesForI18n } from "@/utils/cmsMessages"

const adminBaseUrl = '/cms/admin'
const publicBaseUrl = '/cms/content'

// Module-level cache for i18n instance (set during loadContent, reused by reloadContent)
let i18nInstance = null

/**
 * Convert flat dot-notation keys to a nested object for vue-i18n mergeLocaleMessage
 * e.g. { "home.masthead.headline": "Hello" } -> { home: { masthead: { headline: "Hello" } } }
 */
function unflattenKeys(flatMap) {
  const result = {}

  for (const [key, value] of Object.entries(flatMap)) {
    const parts = key.split('.')
    let current = result

    for (let i = 0; i < parts.length - 1; i++) {
      if (!(parts[i] in current)) {
        current[parts[i]] = {}
      }
      current = current[parts[i]]
    }

    current[parts[parts.length - 1]] = value
  }

  return result
}

export const useCmsContentStore = defineStore('cmsContentStore', () => {
  // State
  const contentLoaded = ref(false)
  const contentMap = ref({}) // raw flat key-value map from API
  const richTextKeys = ref(new Set()) // keys marked as richtext in CMS
  const contentItems = ref([]) // admin listing
  const versions = ref([])
  const loading = ref(false)
  const lastFetchedAt = ref(null)

  // Inline edit mode state
  const editModeEnabled = ref(false)
  const inlineEditKey = ref(null)

  // Initialize showCmsEditFab from localStorage
  const showCmsEditFab = ref(
    localStorage.getItem('showCmsEditFab') === 'true' ? true : false
  )

  /**
   * PUBLIC: Load all content and merge into vue-i18n
   * Called once at app startup — non-blocking, graceful degradation
   */
  async function loadContent(i18n, { bustCache = false } = {}) {
    // Cache i18n instance for reloadContent()
    if (i18n) i18nInstance = i18n

    try {
      const params = { locale: 'en' }

      // Bust browser HTTP cache after admin edits (Cache-Control: max-age=300)
      if (bustCache) params._t = Date.now()

      const res = await axios.get(publicBaseUrl, { params })

      // Support new format { content, richTextKeys } from backend
      const data = res.data
      const flatMap = data.content || data
      const rtKeys = data.richTextKeys || []

      if (flatMap && Object.keys(flatMap).length > 0) {
        contentMap.value = flatMap
        richTextKeys.value = new Set(rtKeys)

        // Convert flat dot-notation keys to nested object and merge into i18n
        // RM-420 (10/22): CMS text reaches vue-i18n made safe (a bare "@" can't crash a screen);
        // contentMap keeps the raw text for CmsText, which renders it without compiling.
        const nested = unflattenKeys(cmsMessagesForI18n(flatMap))
        i18n.global.mergeLocaleMessage('en', nested)

        contentLoaded.value = true
        lastFetchedAt.value = Date.now()
      }
    } catch (error) {
      console.warn('CMS content load failed, using defaults:', error.message)

      // App continues with static en.json defaults — graceful degradation
    }
  }

  /**
   * Get a structured block value by key (for testimonials, FAQs, etc.)
   * Returns the raw value (Array/Object) from the content map
   */
  function getBlock(key) {
    return contentMap.value[key] || null
  }

  /**
   * Reactive computed for a structured block
   */
  function useBlock(key) {
    return computed(() => contentMap.value[key] || null)
  }

  /**
   * Check if a CMS key is richtext (HTML) content.
   * Also detects HTML in static en.json fallback values.
   */
  function isRichText(key, translatedValue) {
    return richTextKeys.value.has(key)
      || (!!translatedValue && /<[a-z][\s\S]*?>/i.test(translatedValue))
  }

  // ===========================
  // ADMIN CRUD METHODS
  // ===========================

  async function getContentItems(filters = {}) {
    try {
      loading.value = true
      const res = await axios.get(adminBaseUrl, {
        params: {
          ...filters,
        },
      })

      if (!res.data) {
        return []
      }

      contentItems.value = res.data

      return res.data
    } catch (error) {
      console.error(error)
      reportError("cmsContent.store.js:getContentItems", error.message, null, null, null, error)

      return []
    } finally {
      loading.value = false
    }
  }

  async function getContentItemById(id) {
    try {
      loading.value = true
      const res = await axios.get(`${adminBaseUrl}/${id}`, {
        params: {
        },
      })

      if (!res.data) {
        return null
      }

      return res.data
    } catch (error) {
      console.error(error)
      reportError("cmsContent.store.js:getContentItemById", error.message, null, null, null, error)

      return null
    } finally {
      loading.value = false
    }
  }

  async function createContentItem(data) {
    try {
      loading.value = true
      const res = await axios.post(adminBaseUrl, data, {
        params: {
        },
      })

      return res.data
    } catch (error) {
      console.error(error)
      reportError("cmsContent.store.js:createContentItem", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function updateContentItem(id, data) {
    try {
      loading.value = true
      const res = await axios.put(`${adminBaseUrl}/${id}`, data, {
        params: {
        },
      })

      return res.data
    } catch (error) {
      console.error(error)
      reportError("cmsContent.store.js:updateContentItem", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function deleteContentItem(id) {
    try {
      loading.value = true
      const res = await axios.delete(`${adminBaseUrl}/${id}`, {
        params: {
        },
      })

      return res.data
    } catch (error) {
      console.error(error)
      reportError("cmsContent.store.js:deleteContentItem", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function getVersions(contentId) {
    try {
      loading.value = true
      const res = await axios.get(`${adminBaseUrl}/${contentId}/versions`, {
        params: {
        },
      })

      versions.value = res.data || []

      return res.data || []
    } catch (error) {
      console.error(error)
      reportError("cmsContent.store.js:getVersions", error.message, null, null, null, error)

      return []
    } finally {
      loading.value = false
    }
  }

  async function rollbackToVersion(contentId, version) {
    try {
      loading.value = true
      const res = await axios.post(`${adminBaseUrl}/${contentId}/rollback/${version}`, {}, {
        params: {
        },
      })

      return res.data
    } catch (error) {
      console.error(error)
      reportError("cmsContent.store.js:rollbackToVersion", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function seedContent(items) {
    try {
      loading.value = true
      const res = await axios.post(`${adminBaseUrl}/seed`, { items }, {
        params: {
        },
      })

      return res.data
    } catch (error) {
      console.error(error)
      reportError("cmsContent.store.js:seedContent", error.message, null, null, null, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // ===========================
  // INLINE EDIT MODE
  // ===========================

  function toggleEditMode() {
    editModeEnabled.value = !editModeEnabled.value
  }

  function toggleCmsEditFab() {
    showCmsEditFab.value = !showCmsEditFab.value
    localStorage.setItem('showCmsEditFab', showCmsEditFab.value.toString())
  }

  function openInlineEdit(key) {
    inlineEditKey.value = key
  }

  function closeInlineEdit() {
    inlineEditKey.value = null
  }

  async function getContentItemByKey(key) {
    try {
      const res = await axios.get(adminBaseUrl, {
        params: {
          search: key,
        },
      })

      if (!res.data) return null

      return res.data.find(item => item.key === key) || null
    } catch (error) {
      console.error(error)
      reportError("cmsContent.store.js:getContentItemByKey", error.message, null, null, null, error)

      return null
    }
  }

  async function reloadContent() {
    if (i18nInstance) {
      await loadContent(i18nInstance, { bustCache: true })
    }
  }

  return {
    // State
    contentLoaded,
    contentMap,
    contentItems,
    versions,
    loading,
    lastFetchedAt,

    // Inline edit mode
    editModeEnabled,
    inlineEditKey,
    showCmsEditFab,
    toggleEditMode,
    toggleCmsEditFab,
    openInlineEdit,
    closeInlineEdit,
    getContentItemByKey,
    reloadContent,

    // Public methods
    loadContent,
    getBlock,
    useBlock,
    isRichText,
    richTextKeys,

    // Admin CRUD
    getContentItems,
    getContentItemById,
    createContentItem,
    updateContentItem,
    deleteContentItem,
    getVersions,
    rollbackToVersion,
    seedContent,
  }
})
